import { BackendApiError } from '@lms/api-client/backend';
import { Input } from '@lms/ui/input';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale, useTranslations } from 'use-intl';
import { z } from 'zod';
import { SubmitButton } from '@/components/submit-button';
import { requestMagicLink } from '@/features/auth/auth-api';
import { useBrand } from '@/features/branding/brand-provider';

const createLoginSchema = z.object({
  email: z.string().email(),
});

export default function LoginForm({ className }: { className?: string }) {
  const t = useTranslations('login');
  const navigate = useNavigate();
  const { slug } = useBrand();
  const language = useLocale();
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const formData = new FormData(e.currentTarget);
    let email: string;
    try {
      email = createLoginSchema.parse({ email: formData.get('email') }).email;
    } catch {
      setError('Ogiltig mailaddress, försök igen');
      return;
    }

    setPending(true);
    try {
      const response = await requestMagicLink({ email, slug, language });
      if (!response.success) {
        setError(t('unknown_email'));
        return;
      }
      navigate(`/${slug}/login/sent?email=${encodeURIComponent(email)}`);
    } catch (err) {
      console.error(err);
      if (err instanceof BackendApiError) {
        setError(t('unknown_email'));
      } else {
        setError('Ogiltig mailaddress, försök igen');
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={className} data-testid="admin-login-form">
      <p className="">{t('enter_email')}</p>
      <form onSubmit={onSubmit}>
        <Input
          type="email"
          placeholder={t('email_placeholder')}
          name="email"
          className="input input-bordered my-4 w-full"
          data-testid="admin-email-input"
        />
        {error && (
          <div className="text-red-500" data-testid="admin-login-error">
            {error}
          </div>
        )}
        <SubmitButton label={t('continue_login')} pending={pending} />
      </form>
    </div>
  );
}
